import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import LoginPage from "./pages/auth/LoginPage";
import Home from "./pages/dashboard/Home";
import AuthGuard from "./components/AuthGuard";
import AuthErrorHandler from "./components/AuthErrorHandler";
import { authService } from "./services/auth";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to get default page based on user role
  const getDefaultPage = () => {
    const userRole = authService.getUserRole();
    if (userRole === "User") {
      return "/home/campaigns"; // User goes to Campaign Management
    }
    return "/home"; // Admin goes to Dashboard
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Wait for auth service to initialize
        await authService.waitForInitialization();

        // Check authentication status
        const authenticated = authService.isUserAuthenticated();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error("Auth initialization failed:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const unsubscribe = authService.addUserDataChangeListener(() => {
      const authenticated = authService.isUserAuthenticated();
      setIsAuthenticated(authenticated);
    });

    // Listen for auth failure events from API service
    const handleAuthFailure = () => {
      console.log("Auth failure detected, redirecting to login");
      setIsAuthenticated(false);
    };

    window.addEventListener("auth-failed", handleAuthFailure);

    return () => {
      unsubscribe();
      window.removeEventListener("auth-failed", handleAuthFailure);
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    console.log("Login successful!");
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      console.log("Logout successful!");
    } catch (error) {
      console.error("Logout error:", error);
      // Force logout even if API call fails
      setIsAuthenticated(false);
    }
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Đang khởi tạo ứng dụng...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthErrorHandler>
      <Router>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to={getDefaultPage()} replace />
              ) : (
                <LoginPage onLoginSuccess={handleLoginSuccess} />
              )
            }
          />
          <Route
            path="/home/*"
            element={
              <AuthGuard>
                <Home onLogout={handleLogout} />
              </AuthGuard>
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to={getDefaultPage()} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to={getDefaultPage()} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Router>
    </AuthErrorHandler>
  );
}

export default App;
